import { Component, OnInit, ViewChild, Inject, Optional } from '@angular/core';
import { Table } from 'primeng/table';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SwalService } from '@shared/utils/swal.service';
import { AbcEmpresasSucursalesFormComponent } from './abc-empresas-sucursales-form/abc-empresas-sucursales-form.component';
import { EmpresasService } from '../../services/empresas.service';
import Swal from 'sweetalert2';
import { ICreateSucursalReq } from '../../interfaces/ISucursales';

@Component({
  selector: 'app-abc-empresas-sucursales',
  templateUrl: './abc-empresas-sucursales.component.html',
  providers: [DialogService]
})
export class AbcEmpresasSucursalesComponent implements OnInit {
  @ViewChild('dt') table!: Table;

  public loading: boolean = false;
  public dataSource: any[] = [];
  public empresas: any[] = [];
  public empresaSeleccionada: any = null;

  columns = [
    { field: 'ssucursal', header: 'Clave' },
    { field: 'nombre_sucursal', header: 'Descripción' },
    { field: 'actions', header: 'Acciones' }
  ];

  constructor(
    @Optional() public config: DynamicDialogConfig,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    @Optional() public dialogRef: MatDialogRef<AbcEmpresasSucursalesComponent>,
    private _swalService: SwalService,
    private dialogService: DialogService,
    private empresasService: EmpresasService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.cargarEmpresas();
  }

  cargarEmpresas(): void {
    this.empresasService.GetEmpresas().subscribe({
      next: (response) => {
        if (response.success) {
          this.empresas = response.data.map(empresa => ({
            label: `${empresa.sEmpresa} - ${empresa.sNombre}`,
            value: empresa.sEmpresa,
            nombre: empresa.sNombre
          }));
          
          // Si viene una empresa específica desde el padre, seleccionarla
          const claveEmpresaPadre = this.config?.data?.claveEmpresa || this.data?.claveEmpresa;
          
          if (claveEmpresaPadre) {
            this.empresaSeleccionada = this.empresas.find(emp => emp.value === claveEmpresaPadre);
            if (this.empresaSeleccionada) {
              this.loadData();
            }
          }
        }
      },
      error: (error) => {
        console.error('Error al cargar empresas:', error);
        this._swalService.error('Error al cargar la lista de empresas');
      }
    });
  }

  onEmpresaChange(): void {
    if (this.empresaSeleccionada) {
      // empresaSeleccionada ya es el objeto completo
      this.loadData();
    } else {
      this.dataSource = [];
    }
  }

  loadData(): void {
    if (!this.empresaSeleccionada) {
      this.dataSource = [];
      return;
    }

    this.loading = true;
    this.empresasService.GetSucursales(this.empresaSeleccionada.value).subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource = response.data;
        } else {
          console.error('Error al cargar sucursales:', response);
          this._swalService.error('Error al cargar la lista de sucursales');
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error al cargar sucursales:', error);
        this._swalService.error('Error al cargar la lista de sucursales');
        this.loading = false;
      }
    });
  }

  nuevo(): void {
    if (!this.empresaSeleccionada) {
      this._swalService.warning('Por favor seleccione una empresa primero');
      return;
    }

    const ref = this.dialogService.open(AbcEmpresasSucursalesFormComponent, {
      header: 'Nueva Sucursal',
      width: '70%',
      data: { 
        claveEmpresa: this.empresaSeleccionada.value
      }
    });

    ref.onClose.subscribe((result: boolean) => {
      if (result) {
        this.loadData();
      }
    });
  }

  ver(row: any): void {
    if (!this.empresaSeleccionada) {
      this._swalService.warning('Por favor seleccione una empresa primero');
      return;
    }

    const ref = this.dialogService.open(AbcEmpresasSucursalesFormComponent, {
      header: 'Editar Sucursal',
      width: '70%',
      data: { 
        empresa: this.empresaSeleccionada.nombre,
        claveEmpresa: this.empresaSeleccionada.value,
        sucursal: row
      }
    });

    ref.onClose.subscribe((result: boolean) => {
      if (result) {
        this.loadData();
      }
    });
  }

  delete(row: any): void {
    Swal.fire({
      title: '¿Está seguro?',
      text: '¿Desea eliminar esta sucursal?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'No',
      heightAuto: false,
      customClass: {
        container: 'swal-index',
      },
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        if (!this.empresaSeleccionada) {
          this._swalService.warning('Por favor seleccione una empresa primero');
          return;
        }

        const payload: ICreateSucursalReq = {
          que: 3,
          clave: this.empresaSeleccionada.value,
          cveSucursal: row.ssucursal,
          nombreSucursal: "",
          ciudad: ""
        };

        this.empresasService.CreateSucursal(payload).subscribe({
          next: (response) => {
            if (response.success && response.data.error === 0) {
              this._swalService.success('Sucursal eliminada correctamente');
              this.loadData();
            } else if (response.data.error === 1) {
              Swal.fire({
                title: 'No es posible eliminar',
                text: 'La sucursal no puede ser eliminada porque está siendo utilizada en otros registros',
                icon: 'error',
                confirmButtonText: 'Aceptar',
                heightAuto: false,
                customClass: {
                  container: 'swal-index',
                },
              });
            } else {
              this._swalService.error('Error al eliminar la sucursal');
            }
            this.loading = false;
          },
          error: (error) => {
            console.error('Error al eliminar sucursal:', error);
            if (error.status === 500 && error.error?.details?.includes('REFERENCE constraint')) {
              Swal.fire({
                title: 'No es posible eliminar',
                text: 'La sucursal no puede ser eliminada porque está siendo utilizada en otros registros',
                icon: 'error',
                confirmButtonText: 'Aceptar',
                heightAuto: false,
                customClass: {
                  container: 'swal-index',
                },
              });
            } else {
              this._swalService.error('Error al eliminar la sucursal');
            }
            this.loading = false;
          }
        });
      }
    });
  }

  // Método para cerrar el modal cuando se usa con Angular Material Dialog
  Close(): void {
    if (this.data?.onClose) {
      this.data.onClose();
    }
    if (this.dialogRef) {
      this.dialogRef.close(true);
    }
  }
}